import type { ReactNode } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import type { ColumnsType } from '@setvi/shared/types';
import moment from 'moment';
import { Customer } from 'Services/Mongo/Types';
import { Stock } from 'pages/products/components/stock';
import { ModifiableProduct } from 'pages/sales/modules/orders/subpages/orders/subpages/order-create';
import { ProductNameCell } from './product-name-cell';
import { QuantityCell } from './quantity-cell';

const formatCurrency = (value: number) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

/**
 * Column captions read as labels, not headings: same 12px regular the catalog
 * header uses (`TableHeaderCell`'s `newDesign` branch). `&&` doubles the class
 * to out-specify that component's own `& p` rule without `!important`.
 */
export const useOrderHistoryHeaderStyles = makeStyles({
  header: {
    '&& p': {
      fontWeight: 400,
      fontSize: 12
    }
  }
});

// A product with a minimum quote quantity moves (and starts) at that quantity.
export const getStep = (product: ModifiableProduct) =>
  product?.minimumQuoteQuantity > 1 &&
  product?.overrideMinimumQuoteQuantity !== true
    ? product.minimumQuoteQuantity
    : 1;

interface BuildColumnsArgs {
  searchQuery?: string;
  selectedProducts: ModifiableProduct[];
  /**
   * The quote's customer, so the Stock column reports what's on hand in *that*
   * account's warehouse rather than the total across all of them. Optional: the
   * product details' ship-to order history renders these columns without one.
   */
  selectedCustomer?: Customer;
  onProductChange: (
    product: ModifiableProduct,
    newValues: Partial<ModifiableProduct>
  ) => void;
  onStockRefresh: (
    id: ModifiableProduct['_id'],
    newValues: Partial<ModifiableProduct>
  ) => void;
  stockLabel?: string;
  // At/below 1180px tighten the column floors so the row fits without a
  // horizontal scroll. Above 1180px the original widths are used.
  isCompact?: boolean;
  /**
   * Grouped layout (the Order History tab): five columns instead of seven, with
   * each secondary value stacked under the primary one it belongs to — stock
   * under the quantity stepper, last quantity under the order date, cost under
   * the price, margin % under the GP. The row selection checkbox moves out to
   * the table's own checkbox column so it lines up with the header's select-all.
   *
   * Off by default: the read-only ship-to order history drops the Quantity
   * column wholesale, which would take Stock with it.
   */
  grouped?: boolean;
  /**
   * `false` ⇒ the tenant has no cost (DEV-795): the "Cost:" line under Last
   * Price and the whole Last GP column come off, in BOTH layouts. Both are
   * derived from a cost the responses no longer carry, so they would otherwise
   * report the entire sell price as profit.
   */
  costAvailable?: boolean;
}

/** Muted caption ahead of a stacked secondary value ("Cost: ", "Qty: "). */
const subLabel = (text: string) => (
  <span style={{ fontWeight: 'normal' }}>{text}</span>
);

/**
 * Body size for the stacked cells. Stated here rather than left to `SText`'s
 * default because MUI's own `TableCell` rule also sets a font size on these
 * cells, and which of the two single-class rules wins depends on stylesheet
 * order. `& p` targets the Typography `SText` renders.
 */
const useStackedCellStyles = makeStyles({
  cell: {
    '& p': {
      fontSize: 16,
      lineHeight: '22px'
    }
  }
});

/** Primary value with its secondary value stacked underneath. */
const StackedCell = ({ children }: { children: ReactNode }) => {
  const classes = useStackedCellStyles();

  return (
    <Box
      className={classes.cell}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gridGap={10}>
      {children}
    </Box>
  );
};

export const buildOrderHistoryColumns = ({
  searchQuery,
  selectedProducts,
  selectedCustomer,
  onProductChange,
  onStockRefresh,
  stockLabel = 'Stock',
  isCompact = false,
  grouped = false,
  costAvailable = true
}: BuildColumnsArgs): ColumnsType<ModifiableProduct> => {
  const quantityOf = (product: ModifiableProduct) => {
    const selected = selectedProducts.find(p => p.sku === product.sku);
    return selected?.quantity ?? product.quantity ?? 0;
  };

  const stockCell = (product: ModifiableProduct) => (
    <Stock
      stockRefresh={{
        id: product._id,
        onStockRefresh: newInventory =>
          onStockRefresh(product._id, { inventory: newInventory })
      }}
      inventory={product.inventory}
      isDropship={product.dropship}
      stockInfo={product.stockInfo}
      isStockProduct={product?.isStockProduct}
      selectedCustomer={selectedCustomer}
    />
  );

  const quantityStepper = (product: ModifiableProduct) => (
    <QuantityCell
      quantity={quantityOf(product)}
      step={getStep(product)}
      onChange={value => onProductChange(product, { quantity: value })}
    />
  );

  const orderDateText = (product: ModifiableProduct) =>
    product?.createdDate
      ? moment(product.createdDate).format('MM/DD/YYYY')
      : '-';

  const gpOf = (product: ModifiableProduct) => {
    const sell = product?.sell ?? 0;
    const cost = product?.cost ?? 0;
    const gp = sell - cost;

    return {
      gp,
      percent: sell > 0 ? (gp / sell) * 100 : 0,
      color: gp >= 0 ? TextColor.Green : TextColor.Red
    };
  };

  // Grouped layout: the stacked-value design, five columns wide. Selection is
  // the table's own checkbox column here, so the name cell renders without one.
  if (grouped)
    return [
      {
        id: 'product',
        label: 'Product Name & SKU',
        minWidth: isCompact ? 280 : 380,
        align: 'left',
        cellContent: (product: ModifiableProduct) => (
          <ProductNameCell product={product} searchQuery={searchQuery} />
        )
      },
      {
        id: 'quantity',
        label: 'Quantity',
        minWidth: isCompact ? 150 : 180,
        align: 'center',
        cellContent: (product: ModifiableProduct) => (
          <StackedCell>
            {quantityStepper(product)}
            {stockCell(product)}
          </StackedCell>
        )
      },
      {
        id: 'orderDate',
        label: 'Order date',
        minWidth: isCompact ? 110 : 130,
        align: 'center',
        cellContent: (product: ModifiableProduct) => (
          <StackedCell>
            <SText title={orderDateText(product)} />
            <SText
              title={(product?.lastSoldQuantity ?? 0).toLocaleString()}
              weight="semibold"
              prefix={subLabel('Qty: ')}
            />
          </StackedCell>
        )
      },
      {
        id: 'lastPrice',
        label: 'Last Price',
        minWidth: isCompact ? 120 : 140,
        align: 'center',
        cellContent: (product: ModifiableProduct) => (
          <StackedCell>
            <SText title={formatCurrency(product?.sell ?? 0)} />
            {/* Only the cost figure is emphasised — its caption and the price
                above it stay regular. Same for "Qty:" on the order date. */}
            {costAvailable && (
              <SText
                title={formatCurrency(product?.cost ?? 0)}
                weight="semibold"
                prefix={subLabel('Cost: ')}
              />
            )}
          </StackedCell>
        )
      },
      ...(costAvailable
        ? [
            {
              id: 'gp',
              // "Last" as on the neighbouring columns: this is the GP of the
              // last order, not of the quote line.
              label: 'Last GP',
              minWidth: isCompact ? 100 : 120,
              align: 'center' as const,
              cellContent: (product: ModifiableProduct) => {
                const { gp, percent, color } = gpOf(product);

                return (
                  <StackedCell>
                    <SText title={formatCurrency(gp)} />
                    <SText
                      title={`(${percent.toFixed(2)}%)`}
                      fontColor={color}
                    />
                  </StackedCell>
                );
              }
            }
          ]
        : [])
    ];

  return [
    {
      id: 'product',
      label: 'Product Name & SKU',
      minWidth: isCompact ? 260 : 360,
      align: 'left',
      cellContent: (product: ModifiableProduct) => {
        const selected = selectedProducts.find(p => p.sku === product.sku);

        return (
          <ProductNameCell
            product={product}
            searchQuery={searchQuery}
            checked={!!selected && (selected.quantity ?? 0) > 0}
            // Quantity IS the selection, so the checkbox just drives it: on puts
            // the product at its minimum quantity, off zeroes it. Ticking it and
            // using +/- therefore stay in sync automatically.
            onCheckedChange={isChecked =>
              onProductChange(product, {
                quantity: isChecked ? getStep(product) : 0
              })
            }
          />
        );
      }
    },
    {
      id: 'stock',
      label: stockLabel,
      minWidth: isCompact ? 90 : 110,
      align: 'center',
      cellContent: (product: ModifiableProduct) => (
        <Box display="flex" justifyContent="center">
          {stockCell(product)}
        </Box>
      )
    },
    {
      id: 'quantity',
      label: 'Quantity',
      minWidth: isCompact ? 140 : 160,
      align: 'center',
      cellContent: (product: ModifiableProduct) => quantityStepper(product)
    },
    {
      id: 'lastQty',
      label: 'Last Qty',
      minWidth: isCompact ? 70 : 80,
      align: 'center',
      cellContent: (product: ModifiableProduct) => (
        <Box display="flex" justifyContent="center">
          <SText
            title={(product?.lastSoldQuantity ?? 0).toLocaleString()}
            weight="semibold"
          />
        </Box>
      )
    },
    {
      id: 'lastDate',
      label: 'Last Date',
      minWidth: isCompact ? 90 : 110,
      align: 'center',
      cellContent: (product: ModifiableProduct) => (
        <Box display="flex" justifyContent="center">
          <SText title={orderDateText(product)} />
        </Box>
      )
    },
    {
      id: 'lastPrice',
      label: 'Last Price',
      minWidth: isCompact ? 110 : 130,
      align: 'center',
      cellContent: (product: ModifiableProduct) => (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gridGap={2}>
          <SText
            title={formatCurrency(product?.sell ?? 0)}
            weight="semibold"
            fontColor={TextColor.Blue}
          />
          {costAvailable && (
            <SText
              title={formatCurrency(product?.cost ?? 0)}
              prefix={<span style={{ fontWeight: 'normal' }}>{`Cost: `}</span>}
            />
          )}
        </Box>
      )
    },
    ...(costAvailable
      ? [
          {
            id: 'lastGp',
            label: 'Last GP',
            minWidth: isCompact ? 90 : 100,
            align: 'center' as const,
            cellContent: (product: ModifiableProduct) => {
              const sell = product?.sell ?? 0;
              const cost = product?.cost ?? 0;
              const gp = sell - cost;
              const gpPercent = sell > 0 ? (gp / sell) * 100 : 0;
              const color = gp >= 0 ? TextColor.Green : TextColor.Red;
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gridGap={2}>
                  <SText
                    title={formatCurrency(gp)}
                    weight="semibold"
                    fontColor={color}
                  />
                  <SText
                    title={`${gpPercent.toFixed(2)}%`}
                    weight="semibold"
                    fontColor={color}
                  />
                </Box>
              );
            }
          }
        ]
      : [])
  ];
};
